<template>
  <div>
    <Steps :active="1" />
    <form @submit.prevent="send">
      <div class="flex flex-col md:w-8/12 mx-auto">
        <div class="font-bold text-2xl text-gray-700 mx-auto mb-4">Order Number and Order Delivery Postcode</div>

        <ValidationProvider
          name="Order Number"
          rules="required"
          v-slot="{ errors }"
          class="flex flex-col mx-auto w-full mb-4 md:w-6/12"
        >
          <input
            ref="orderId"
            v-model="orderId"
            class="border rounded p-4 text-center text-xl shadow-lg"
            :class="{'border-red-600': errors[0]}"
            placeholder="Order Number"
            required="true"
            tabindex="1"
            autofocus
          />
          <div class="text-red-600 mt-2" v-if="errors[0]">
            <small>{{ errors[0] }}</small>
          </div>
        </ValidationProvider>
        <ValidationProvider
          name="Order Number"
          rules="required"
          v-slot="{ errors }"
          class="flex flex-col mx-auto w-full mb-4 md:w-6/12"
        >
          <input
            class="border rounded p-4 text-center text-xl shadow-lg uppercase"
            :class="{'border-red-600': errors[0]}"
            placeholder="Order Delivery Postcode"
            required="true"
            v-model="postcode"
            tabindex="1"
          />
          <div class="text-red-600 mt-2" v-if="errors[0]">
            <small>{{ errors[0] }}</small>
          </div>
          <div class="text-gray-600 mt-2">
            <small>
              <strong class="font-bold">Note:</strong> This is the delivery postcode of the order, not the seller's.
            </small>
          </div>
        </ValidationProvider>
      </div>

      <div class="flex justify-center mb-8">
        <ButtonNext
          :class="{'bg-gray-700': orderId && postcode && !processing}"
          :disabled="!orderId && !postcode || processing"
        />
      </div>

      <div class="flex flex-col justify-center items-center mb-4">
        <a href="javascript:;" @click="showHelp=!showHelp" class="text-gray-600">Need help finding your order number?</a>
      </div>
      <div
        class="flex flex-col rounded p-10 bg-white w-full mx-auto shadow-lg md:w-6/12"
        :class="(showHelp ? 'flex' : 'hidden')"
      >
        <div class="font-bold text-2xl text-gray-700 mb-6">Supported Websites</div>

        <div class="flex flex-col md:flex-row">
          <div class="w-full sm:w-6/12 mb-4">
            <div class="font-bold text-gray-800 mb-1">Amazon</div>
            <div class="mb-1">203-1234567-1234567</div>
            <div class="text-xs text-gray-500">3 numbers - 7 numbers - 7 numbers</div>
          </div>
          <div class="w-full sm:w-6/12 mb-4">
            <div class="font-bold text-gray-800 mb-1">B&Q</div>
            <div class="mb-1">1025165229-A</div>
            <div class="text-xs text-gray-500">10 numbers - 1 letter</div>
          </div>
        </div>

        <div class="flex flex-col md:flex-row">
          <div class="w-full sm:w-6/12 mb-4">
            <div class="font-bold text-gray-800 mb-1">Debenhams</div>
            <div class="mb-1">DUX111222333-A</div>
            <div class="text-xs text-gray-500">3 letters - 9 numbers - 1 letter</div>
          </div>
          <div class="w-full sm:w-6/12 mb-4">
            <div class="font-bold text-gray-800 mb-1">eBay</div>
            <div class="mb-1">20-12345-67890</div>
            <div class="text-xs text-gray-500">2 numbers - 5 numbers - 5 numbers</div>
          </div>
        </div>

        <div class="flex flex-col md:flex-row">
          <div class="w-full sm:w-6/12 mb-4">
            <div class="font-bold text-gray-800 mb-1">Groupon</div>
            <div class="mb-1">123456789</div>
            <div class="text-xs text-gray-500">9 or 10 numbers</div>
          </div>
          <div class="w-full sm:w-6/12 mb-4">
            <div class="font-bold text-gray-800 mb-1">OnBuy</div>
            <div class="mb-1">T123A4</div>
            <div class="text-xs text-gray-500">6 letters/numbers</div>
          </div>
        </div>

        <div class="flex flex-col md:flex-row">
          <div class="w-full sm:w-6/12 mb-4">
            <div class="font-bold text-gray-800 mb-1">Oypla</div>
            <div class="mb-1">1234567</div>
            <div class="text-xs text-gray-500">7 numbers</div>
          </div>
          <div class="w-full sm:w-6/12 mb-4">
            <div class="font-bold text-gray-800 mb-1">Wowcher Voucher</div>
            <div class="mb-1">1234AB-CDEF5G</div>
            <div class="text-xs text-gray-500">6 letters/numbers - 6 letters/numbers</div>
          </div>
        </div>

        <div
          class="text-gray-600"
        >If you still can't find your order number, please contact the seller you placed your order with.</div>
      </div>
    </form>
  </div>
</template>

<script>
import ButtonNext from '@/components/ButtonNext.vue'
import Steps from '@/components/Steps.vue'
import { mapActions } from 'vuex'
import { ValidationProvider } from 'vee-validate'

export default {
  components: {
    ButtonNext,
    Steps,
    ValidationProvider,
  },
  data() {
    return {
      orderId: '',
      postcode: '',
      showHelp: false,
      processing: false,
    }
  },
  mounted() {
    this.$nextTick(() => this.$refs.orderId.focus())
    this.reset()
  },
  methods: {
    ...mapActions(['getOrderDetails', 'reset']),
    send() {
      if (typeof this.$recaptcha === 'undefined') {
        this.$snotify.error(
          "Couldn't load Recaptcha. Make sure that your AdBlocker is disabled and you are connected to the internet."
        )
        return
      }

      this.$recaptcha('homepage')
        .then(token => {
          this.processing = true
          this.$snotify.info('Processing your request...')
          this.getOrderDetails({
            orderId: this.orderId,
            postcode: this.postcode,
            recaptchaToken: token,
          })
            .then(() => this.$router.push('step2'))
            .catch(err => {
              this.processing = false
              this.$snotify.error(err)
            })
        })
        .catch(err => this.$snotify.error(err))
    },
  },
}
</script>
